import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Dropdown,
  EmptyState,
  formatDate,
  IconButton,
  Inline,
  Modal,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableLoading,
  TableRow,
  Text,
  useToast,
  VisuallyHidden,
  type BadgeTone,
} from '@hkdevs/design-system';
import { useState } from 'react';
import { ApiError } from '../../api/client';
import { useAdminNotices, useNoticeMutations, type AdminNotice } from '../../api/queries/notices';
import { PageLayout } from '../../shell/Page';
import {
  audienceLabel,
  emptyNotice,
  NoticeEditor,
  noticeFormErrors,
  noticeValues,
  toDate,
  toNoticeBody,
  type NoticeFormErrors,
  type NoticeFormValues,
} from './NoticeEditor';
import { withReference } from '../../api/withReference';

/** The admin's Notices page (pc-dbb.18): every notice, New notice, Edit, End now, Delete. */

type NoticeStatus = AdminNotice['status'];

const STATUS_BADGES: Record<NoticeStatus, { label: string; tone: BadgeTone }> = {
  scheduled: { label: 'Scheduled', tone: 'info' },
  active: { label: 'Active', tone: 'success' },
  expired: { label: 'Expired', tone: 'neutral' },
};

const TONE_BADGES: Record<AdminNotice['tone'], { label: string; tone: BadgeTone }> = {
  info: { label: 'Info', tone: 'info' },
  warning: { label: 'Warning', tone: 'warning' },
  danger: { label: 'Urgent', tone: 'danger' },
};

const STATUS_ORDER: Record<NoticeStatus, number> = { active: 0, scheduled: 1, expired: 2 };

/** Active first, then scheduled, then expired; the latest start first within each. */
export function sortNotices(notices: readonly AdminNotice[]) {
  return [...notices].sort(
    (a, b) =>
      STATUS_ORDER[a.status] - STATUS_ORDER[b.status] || b.startsOn.localeCompare(a.startsOn),
  );
}

/** When it shows: "10/01/2026 – 10/15/2026", "From 10/01/2026, until acknowledged". */
export function showsLabel(notice: Pick<AdminNotice, 'startsOn' | 'endsOn'>) {
  const start = formatDate(notice.startsOn);
  if (notice.endsOn === null) return `From ${start}, until acknowledged`;
  return `${start} – ${formatDate(notice.endsOn)}`;
}

/** "37 of 52 users", or "Not required". */
export function acknowledgedLabel(notice: AdminNotice) {
  if (!notice.requiresAck) return 'Not required';
  return `${notice.acknowledged} of ${notice.audience} ${notice.audience === 1 ? 'user' : 'users'}`;
}

type Editing = { mode: 'create' } | { mode: 'edit'; notice: AdminNotice } | null;
type Confirming = { kind: 'end' | 'delete'; notice: AdminNotice } | null;

/** The page: its header's New notice, and the list. */
export function NoticesPage() {
  const { toast } = useToast();
  const notices = useAdminNotices();
  const { create, update, end, remove } = useNoticeMutations();
  const [editing, setEditing] = useState<Editing>(null);
  const [confirming, setConfirming] = useState<Confirming>(null);
  const [serverErrors, setServerErrors] = useState<NoticeFormErrors>();
  const today = notices.data ? toDate(notices.data.today) : new Date();

  const openEditor = (next: Editing) => {
    setServerErrors(undefined);
    setEditing(next);
  };

  const save = (values: NoticeFormValues) => {
    setServerErrors(undefined);
    const body = toNoticeBody(values);
    const done = (notice: AdminNotice) => {
      toast({
        tone: 'success',
        title: editing?.mode === 'edit' ? 'Notice saved' : `“${notice.title}” is posted`,
      });
      setEditing(null);
    };
    const onError = (error: unknown) => {
      const errors = noticeFormErrors(error);
      if (errors) setServerErrors(errors);
      else
        toast(
          withReference(
            {
              tone: 'danger',
              title: 'The notice couldn’t be saved',
              description: 'Check your connection and try again.',
            },
            error,
          ),
        );
    };
    if (editing?.mode === 'edit') {
      update.mutate({ id: editing.notice.id, body }, { onSuccess: done, onError });
    } else {
      create.mutate(body, { onSuccess: done, onError });
    }
  };

  const confirm = () => {
    if (!confirming) return;
    const { kind, notice } = confirming;
    setConfirming(null);
    const failed = (error: unknown) =>
      toast(
        withReference(
          {
            tone: 'danger',
            title:
              kind === 'end' ? 'The notice couldn’t be ended' : 'The notice couldn’t be deleted',
            description:
              error instanceof ApiError && error.status < 500 && error.problem.detail
                ? error.problem.detail
                : 'Check your connection and try again.',
          },
          error,
        ),
      );
    if (kind === 'end') {
      end.mutate(notice.id, {
        onSuccess: () => toast({ tone: 'success', title: `“${notice.title}” has ended` }),
        onError: failed,
      });
    } else {
      remove.mutate(notice.id, {
        onSuccess: () => toast({ tone: 'success', title: `“${notice.title}” is deleted` }),
        onError: failed,
      });
    }
  };

  let body;
  if (notices.isError) {
    body = (
      <Alert
        tone="danger"
        title="Notices couldn’t be loaded"
        action={
          <Button size="sm" variant="secondary" onClick={() => void notices.refetch()}>
            Retry
          </Button>
        }
      >
        Check your connection and try again.
      </Alert>
    );
  } else if (notices.data && notices.data.items.length === 0) {
    body = (
      <Card>
        <CardBody>
          <EmptyState
            title="No notices"
            description="Post a notice to tell everyone about a change, an outage or a deadline. It shows at the top of every page."
            icon="megaphone"
            headingLevel={2}
            actions={
              <Button size="sm" leadingIcon="plus" onClick={() => openEditor({ mode: 'create' })}>
                New notice
              </Button>
            }
          />
        </CardBody>
      </Card>
    );
  } else {
    body = (
      <Card flush>
        <Table aria-label="Notices" responsive="cards" busy={!notices.data}>
          <TableHead>
            <TableCell>Notice</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Audience</TableCell>
            <TableCell>Shows</TableCell>
            <TableCell>Acknowledged</TableCell>
            <TableCell width="44px">
              <VisuallyHidden>Actions</VisuallyHidden>
            </TableCell>
          </TableHead>
          <TableBody>
            {!notices.data ? (
              <TableLoading colSpan={6} rows={4} label="Loading notices" />
            ) : (
              sortNotices(notices.data.items).map((notice) => (
                <TableRow key={notice.id}>
                  <TableCell rowHeader>
                    <Stack as="span" gap={1}>
                      <Text weight="semibold">{notice.title}</Text>
                      <Inline as="span" gap={2} rowGap={1}>
                        <Badge tone={TONE_BADGES[notice.tone].tone} size="sm">
                          <VisuallyHidden>Tone: </VisuallyHidden>
                          {TONE_BADGES[notice.tone].label}
                        </Badge>
                        {notice.blocking && (
                          <Text tone="muted">Blocks the app until acknowledged</Text>
                        )}
                      </Inline>
                    </Stack>
                  </TableCell>
                  <TableCell label="Status">
                    <Badge tone={STATUS_BADGES[notice.status].tone} size="sm" dot>
                      {STATUS_BADGES[notice.status].label}
                    </Badge>
                  </TableCell>
                  <TableCell label="Audience" tone="muted">
                    {audienceLabel(notice.targetRoles)}
                  </TableCell>
                  <TableCell label="Shows" tone="muted">
                    {showsLabel(notice)}
                  </TableCell>
                  <TableCell label="Acknowledged" tone="muted">
                    {acknowledgedLabel(notice)}
                  </TableCell>
                  <TableCell>
                    <Dropdown
                      align="end"
                      trigger={
                        <IconButton
                          icon="more-vertical"
                          size="sm"
                          variant="ghost"
                          aria-label={`Actions for ${notice.title}`}
                        />
                      }
                      items={[
                        { label: 'Edit', onClick: () => openEditor({ mode: 'edit', notice }) },
                        // End now ends an active notice; one that hasn't started is deleted.
                        ...(notice.status === 'active'
                          ? [
                              {
                                label: 'End now',
                                onClick: () => setConfirming({ kind: 'end', notice }),
                              },
                            ]
                          : []),
                        {
                          label: 'Delete',
                          danger: true,
                          onClick: () => setConfirming({ kind: 'delete', notice }),
                        },
                      ]}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>
    );
  }

  return (
    <PageLayout
      page="notices"
      actions={
        <Button size="sm" leadingIcon="plus" onClick={() => openEditor({ mode: 'create' })}>
          New notice
        </Button>
      }
    >
      {body}
      <Modal
        open={!!confirming}
        onClose={() => setConfirming(null)}
        size="sm"
        title={
          confirming &&
          (confirming.kind === 'end'
            ? `End “${confirming.notice.title}” now?`
            : `Delete “${confirming.notice.title}”?`)
        }
        description={
          confirming?.kind === 'end'
            ? 'It stops showing to everyone now. It stays here as Expired, with its acknowledgements.'
            : 'It stops showing, and who acknowledged it is deleted with it. This can’t be undone.'
        }
        footer={
          confirming && (
            <>
              <Button variant="secondary" onClick={() => setConfirming(null)}>
                Cancel
              </Button>
              <Button variant="danger" onClick={confirm}>
                {confirming.kind === 'end' ? 'End notice' : 'Delete notice'}
              </Button>
            </>
          )
        }
      />
      {editing && (
        <NoticeEditor
          key={editing.mode === 'edit' ? editing.notice.id : 'new'}
          onClose={() => setEditing(null)}
          mode={editing.mode}
          initialValues={
            editing.mode === 'edit' ? noticeValues(editing.notice) : emptyNotice(today)
          }
          today={today}
          onSubmit={save}
          submitting={create.isPending || update.isPending}
          serverErrors={serverErrors}
        />
      )}
    </PageLayout>
  );
}
